import type { Status } from "../../stock/model";

// Each shop keeps its colour wherever it is drawn, in the order of the shops' list (categorical slots, checked for
// colour-blind separation on white). A shop the sheet adds later takes the next slot.
const SHOP_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"];
export const shopColor = (shops: string[], shop: string) => SHOP_COLORS[Math.max(0, shops.indexOf(shop)) % SHOP_COLORS.length]!;
/** Short names for narrow columns. */
export const shopShort = (shop: string) => ({ "Steen & Strøm": "S&S", Grünerløkka: "Grünerl.", Bogstadveien: "Bogstadv." })[shop] ?? shop;

// Status always comes with its word and a mark, never colour alone.
// `short` is the word on a phone, where the grid is narrow.
export const STATUS: Record<Status, { label: string; short: string; mark: string; pill: string; bar: string; rank: number }> = {
  order: { label: "Order now", short: "Order", mark: "!", pill: "bg-[#fbe4e4] text-[#9b2121]", bar: "#d03b3b", rank: 0 },
  soon: { label: "Order soon", short: "Soon", mark: "◷", pill: "bg-[#fdf0d2] text-[#7a5200]", bar: "#fab219", rank: 1 },
  ok: { label: "OK", short: "OK", mark: "✓", pill: "bg-[#e3f4e3] text-[#1d6b1d]", bar: "#0ca30c", rank: 2 },
  idle: { label: "No forecast", short: "None", mark: "–", pill: "bg-[#f3eef0] text-[#7a6a70]", bar: "#c9bcc1", rank: 3 },
};

const day = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const weekday = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
const utc = (iso: string) => new Date(`${iso.slice(0, 10)}T${iso.length > 10 ? iso.slice(11, 19) : "12:00:00"}Z`);

/** "16 Oct" */
export const shortDate = (iso: string | null | undefined) => (iso ? day.format(utc(iso)) : "–");
/** "Mon 26 Oct" */
export const dayDate = (iso: string) => weekday.format(utc(iso));
/** "09:35", from the sheet's own clock */
export const clock = (iso: string) => time.format(utc(iso));
export const daysAgo = (iso: string, today: string) => Math.round((utc(today).getTime() - utc(iso.slice(0, 10)).getTime()) / 86_400_000);
export const agoText = (days: number) => (days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`);

/** The ISO week number of a Monday. */
export function weekNumber(monday: string) {
  const thursday = utc(monday).getTime() + 3 * 86_400_000;
  const yearStart = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1);
  return Math.ceil(((thursday - yearStart) / 86_400_000 + 1) / 7);
}

const whole = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const tenth = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 });
/** 6480 → "6,480"; small amounts keep a tenth. */
export const fmt = (n: number | null | undefined) => (n === null || n === undefined ? "–" : Math.abs(n) < 10 && n % 1 ? tenth.format(n) : whole.format(n));
